import { extractFileNameFromThreadAttachment, type TaskChatAuthorInfo } from '@frontend/api-client';
import type { SavedFile, ThreadMessageAttachment } from '@frontend/api-types';
import { makeSavedFileList } from '@frontend/forms';
import { useState, useVisibilityController } from '@frontend/react';
import { BlinkingDot, FilePreviewModal, YStack } from '@frontend/web-react';
import { TaskChatAuthor } from '../Tasks';
import { ChatFilePreview } from '../Tasks/Chat/ChatFilePreview';
import type { ThreadMarkdownRendererProps } from './ThreadMarkdownRenderer';
import { ThreadMarkdownRenderer } from './ThreadMarkdownRenderer';

export type ThreadChatMessageBubbleProps = Pick<ThreadMarkdownRendererProps, 'onClickLink'> & {
  attachments?: ThreadMessageAttachment[];
  authorInfo: TaskChatAuthorInfo;
  content: string;
  isStreaming?: boolean;
  threadId: string;
};

export const ThreadChatMessageBubble = ({
  attachments,
  authorInfo,
  content,
  isStreaming,
  onClickLink,
  threadId,
}: ThreadChatMessageBubbleProps) => {
  const [isFilePreviewModalOpen, { close: closeFilePreviewModal, open: openFilePreviewModal }] =
    useVisibilityController();
  const [selectedAttachment, setSelectedAttachment] = useState<SavedFile>();
  const savedAttachments = makeSavedFileList(attachments ?? [], (a) => ({
    ...a,
    key: extractFileNameFromThreadAttachment(a),
    url: a.fileUrl,
  }));
  return (
    <YStack gapY="md">
      <TaskChatAuthor isThread {...authorInfo} />
      <YStack className="pl-xl" gapY="sm">
        <ThreadMarkdownRenderer
          markdownContent={content}
          onClickLink={onClickLink}
          preserveNewlines
          threadId={threadId}
        />
        {isStreaming ? <BlinkingDot /> : null}
        <YStack className="mt-sm" gapY="md">
          {savedAttachments?.map((attachment) => (
            <ChatFilePreview
              file={attachment}
              key={attachment.key}
              onClick={(file) => {
                setSelectedAttachment(file);
                openFilePreviewModal();
              }}
            />
          ))}
        </YStack>
      </YStack>
      <FilePreviewModal
        file={selectedAttachment}
        isOpen={isFilePreviewModalOpen}
        onClose={closeFilePreviewModal}
        title={selectedAttachment?.key}
      />
    </YStack>
  );
};
